import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { TextField, Button, Typography, Container, MenuItem, FormControlLabel, Checkbox, Box, FormHelperText, Alert } from '@mui/material';
import MaskedInput from './MaskedInput';
import { DEFAULT_TIMEZONE } from '../utils/dateTime';
import FormCard from './FormCard';

function EmpresaForm({ onEmpresaAdded, initialData, onCancelEdit }) {
  const [id, setId] = useState(null);
  const [nome, setNome] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const [whatsappApiKey, setWhatsappApiKey] = useState('');
  const [whatsappInstance, setWhatsappInstance] = useState('');
  const [whatsappMensagemRodape, setWhatsappMensagemRodape] = useState('');
  const [endereco, setEndereco] = useState('');
  const [numero, setNumero] = useState('');
  const [bairro, setBairro] = useState('');
  const [relatorioTurnoModo, setRelatorioTurnoModo] = useState('marcacao');
  const [relatorioTurnoHorarios, setRelatorioTurnoHorarios] = useState([]);

  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [whatsappQr, setWhatsappQr] = useState(null);
  const [whatsappPairing, setWhatsappPairing] = useState(null);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappError, setWhatsappError] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');

  const SLOTS_HORARIOS = ['06:00', '08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00', '22:00'];

  const suggestedInstanceName = id ? `cnd-${id}` : '';

  const fetchWhatsappStatus = useCallback(() => {
    if (!id) return;
    api.get('/whatsapp/instancia/estado', { params: { empresa_id: id } })
      .then((response) => setWhatsappStatus(response.data))
      .catch(() => { /* estado indisponível — ignora silenciosamente */ });
  }, [id]);

  useEffect(() => {
    if (initialData) {
      setId(initialData.id);
      setNome(initialData.nome);
      setCnpj(initialData.cnpj);
      setTimezone(initialData.timezone || DEFAULT_TIMEZONE);
      setWhatsappApiKey(initialData.whatsapp_api_key || '');
      setWhatsappInstance(initialData.whatsapp_instance || '');
      setWhatsappNumber(initialData.whatsapp_numero || '');
      setWhatsappMensagemRodape(initialData.whatsapp_mensagem_rodape || '');
      setEndereco(initialData.endereco || '');
      setNumero(initialData.numero || '');
      setBairro(initialData.bairro || '');
      setRelatorioTurnoModo(initialData.relatorio_turno_modo || 'marcacao');
      const horariosStr = initialData.relatorio_turno_horarios || '';
      setRelatorioTurnoHorarios(horariosStr ? horariosStr.split(',').map((t) => t.trim()).filter(Boolean) : []);
    } else {
      clearForm();
    }
  }, [initialData]);

  useEffect(() => {
    if (!id || !whatsappInstance) return undefined;
    if (whatsappStatus?.state === 'open') return undefined;
    fetchWhatsappStatus();
    const interval = setInterval(fetchWhatsappStatus, 4000);
    return () => clearInterval(interval);
  }, [id, whatsappInstance, whatsappStatus?.state, fetchWhatsappStatus]);

  const clearForm = () => {
    setId(null);
    setNome('');
    setCnpj('');
    setTimezone(DEFAULT_TIMEZONE);
    setWhatsappApiKey('');
    setWhatsappInstance('');
    setWhatsappMensagemRodape('');
    setEndereco('');
    setNumero('');
    setBairro('');
    setRelatorioTurnoModo('marcacao');
    setRelatorioTurnoHorarios([]);
    setWhatsappStatus(null);
    setWhatsappQr(null);
    setWhatsappPairing(null);
    setWhatsappError('');
    setWhatsappNumber('');
  };

  const handleCreateInstance = () => {
    setWhatsappLoading(true);
    setWhatsappError('');
    const instanceName = whatsappInstance || suggestedInstanceName;

    api.post('/whatsapp/instancia', { instance_name: instanceName, empresa_id: id, number: whatsappNumber })
      .then((response) => {
        setWhatsappInstance(response.data.instance_name || instanceName);
        setWhatsappApiKey(response.data.api_key || '');
        const qrcode = response.data.qrcode || null;
        setWhatsappQr(qrcode);
        setWhatsappPairing(qrcode ? (qrcode.code || qrcode.pairingCode || null) : null);
        fetchWhatsappStatus();
      })
      .catch((error) => {
        setWhatsappError(error.response?.data?.message || error.message || 'Erro ao criar instância.');
      })
      .finally(() => setWhatsappLoading(false));
  };

  const handleGenerateQr = () => {
    setWhatsappLoading(true);
    setWhatsappError('');
    api.get('/whatsapp/instancia/qrcode', { params: { empresa_id: id } })
      .then((response) => {
        const qrcode = response.data.qrcode || null;
        setWhatsappQr(qrcode);
        setWhatsappPairing(qrcode ? (qrcode.code || qrcode.pairingCode || null) : null);
      })
      .catch((error) => {
        setWhatsappError(error.response?.data?.message || error.message || 'Erro ao gerar QR code.');
      })
      .finally(() => setWhatsappLoading(false));
  };

  const handleDeleteInstance = () => {
    if (!window.confirm('Tem certeza que deseja excluir a instância WhatsApp? Será necessário criar e conectar novamente.')) {
      return;
    }
    setWhatsappLoading(true);
    setWhatsappError('');
    api.delete('/whatsapp/instancia', { params: { empresa_id: id } })
      .then(() => {
        setWhatsappInstance('');
        setWhatsappApiKey('');
        setWhatsappNumber('');
        setWhatsappQr(null);
        setWhatsappPairing(null);
        setWhatsappStatus(null);
      })
      .catch((error) => {
        setWhatsappError(error.response?.data?.message || error.message || 'Erro ao excluir instância.');
      })
      .finally(() => setWhatsappLoading(false));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const empresaData = { nome, cnpj, timezone, whatsapp_api_key: whatsappApiKey, whatsapp_instance: whatsappInstance, whatsapp_numero: whatsappNumber, whatsapp_mensagem_rodape: whatsappMensagemRodape, endereco, numero, bairro, relatorio_turno_modo: relatorioTurnoModo, relatorio_turno_horarios: relatorioTurnoModo === 'horario' ? relatorioTurnoHorarios.join(',') : null };

    if (id) {
      // Modo de edicao
      api.put(`/empresas/${id}`, empresaData)
        .then(response => {
          console.log('Empresa atualizada:', response.data);
          clearForm();
          onEmpresaAdded(); // Notifica o componente pai para atualizar a lista
          onCancelEdit(); // Sai do modo de edicao
        })
        .catch(error => {
          console.error('Erro ao atualizar empresa:', error);
        });
    } else {
      // Modo de criacao
      api.post('/empresas', empresaData)
        .then(response => {
          console.log('Empresa adicionada:', response.data);
          clearForm();
          onEmpresaAdded(); // Notifica o componente pai para atualizar a lista
        })
        .catch(error => {
          console.error('Erro ao adicionar empresa:', error);
        });
    }
  };

  return (
    <Container maxWidth="sm">
      <FormCard component="form" onSubmit={handleSubmit}>
        <Typography variant="h5" gutterBottom>
          {id ? 'Editar Empresa' : 'Adicionar Nova Empresa'}
        </Typography>
        <TextField
          label="Nome da Empresa"
          variant="outlined"
          fullWidth
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          margin="normal"
          required
        />
        <TextField
          label="Timezone"
          variant="outlined"
          fullWidth
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          margin="normal"
          required
          helperText="Use um timezone IANA, por exemplo: America/Sao_Paulo"
        />
        {id && (
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              WhatsApp (Evolution API)
            </Typography>
            <TextField
              label="Instância WhatsApp"
              variant="outlined"
              fullWidth
              value={whatsappInstance || suggestedInstanceName}
              disabled
              margin="normal"
              helperText="Nome da instância gerado automaticamente (não editável)."
            />
            <TextField
              label="Número do WhatsApp"
              variant="outlined"
              fullWidth
              value={whatsappNumber}
              onChange={(e) => setWhatsappNumber(e.target.value)}
              margin="normal"
              placeholder="5511999999999"
              disabled={Boolean(whatsappInstance)}
              helperText="Número que será conectado, com código do país (ex.: 5511999999999). Informado apenas ao criar a instância."
            />
            <TextField
              label="WhatsApp API Key"
              variant="outlined"
              fullWidth
              value={whatsappApiKey}
              disabled
              margin="normal"
              helperText="Preenchida automaticamente ao criar a instância."
            />
            {whatsappStatus?.state && (
              <Typography
                variant="body2"
                color={whatsappStatus.state === 'open' ? 'success.main' : 'text.secondary'}
                sx={{ mt: 1 }}
              >
                Status da conexão: {whatsappStatus.state}
              </Typography>
            )}
            {whatsappError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {whatsappError}
              </Alert>
            )}
            {whatsappQr?.base64 && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <img
                  src={whatsappQr.base64}
                  alt="QR Code WhatsApp"
                  style={{ maxWidth: 220, border: '1px solid #ddd', borderRadius: 8 }}
                />
                {whatsappPairing && (
                  <Typography variant="body2" sx={{ mt: 1, wordBreak: 'break-all' }}>
                    Código de pareamento: <strong>{whatsappPairing}</strong>
                  </Typography>
                )}
              </Box>
            )}
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                type="button"
                variant="contained"
                color="primary"
                disabled={whatsappLoading}
                onClick={whatsappInstance ? handleGenerateQr : handleCreateInstance}
              >
                {whatsappLoading ? 'Processando...' : (whatsappInstance ? 'Gerar novo QR' : 'Criar instância')}
              </Button>
              {whatsappInstance && (
                <Button type="button" variant="outlined" onClick={fetchWhatsappStatus}>
                  Atualizar status
                </Button>
              )}
              {whatsappInstance && (
                <Button
                  type="button"
                  variant="outlined"
                  color="error"
                  disabled={whatsappLoading}
                  onClick={handleDeleteInstance}
                >
                  Excluir instância
                </Button>
              )}
            </Box>
          </Box>
        )}
        <TextField
          label="Mensagem de Rodapé WhatsApp"
          variant="outlined"
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          value={whatsappMensagemRodape}
          onChange={(e) => setWhatsappMensagemRodape(e.target.value)}
          margin="normal"
          helperText="Mensagem exibida ao final das notificações de encomenda. Se vazio, não haverá mensagem."
        />
        <TextField
          select
          label="Relatório de Turno"
          variant="outlined"
          fullWidth
          value={relatorioTurnoModo}
          onChange={(e) => setRelatorioTurnoModo(e.target.value)}
          margin="normal"
          helperText="Como o síndico recebe o resumo das atividades da portaria"
        >
          <MenuItem value="marcacao">A cada marcação de ponto</MenuItem>
          <MenuItem value="horario">Em horários definidos</MenuItem>
        </TextField>
        {relatorioTurnoModo === 'horario' && (
          <Box sx={{ mt: 1, mb: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Horários de envio do relatório consolidado:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {SLOTS_HORARIOS.map((slot) => (
                <FormControlLabel
                  key={slot}
                  control={
                    <Checkbox
                      size="small"
                      checked={relatorioTurnoHorarios.includes(slot)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setRelatorioTurnoHorarios([...relatorioTurnoHorarios, slot].sort());
                        } else {
                          setRelatorioTurnoHorarios(relatorioTurnoHorarios.filter((h) => h !== slot));
                        }
                      }}
                    />
                  }
                  label={slot}
                />
              ))}
            </Box>
            {relatorioTurnoHorarios.length === 0 && (
              <FormHelperText error>Selecione ao menos um horário.</FormHelperText>
            )}
          </Box>
        )}
        <TextField
          label="Endereço"
          variant="outlined"
          fullWidth
          value={endereco}
          onChange={(e) => setEndereco(e.target.value)}
          margin="normal"
        />
        <TextField
          label="Número"
          variant="outlined"
          fullWidth
          value={numero}
          onChange={(e) => setNumero(e.target.value)}
          margin="normal"
        />
        <TextField
          label="Bairro"
          variant="outlined"
          fullWidth
          value={bairro}
          onChange={(e) => setBairro(e.target.value)}
          margin="normal"
        />
        <TextField
          label="CNPJ"
          variant="outlined"
          fullWidth
          value={cnpj}
          onChange={(e) => setCnpj(e.target.value)}
          margin="normal"
          required
          InputLabelProps={{ shrink: true }} // Added shrink prop
          InputProps={{
            inputComponent: MaskedInput,
            inputProps: {
              mask: "00.000.000/0000-00",
              definitions: {
                '#': /[0-9]/,
              },
              placeholderChar: "_",
              unmask: true,
            },
          }}
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          sx={{ mt: 2, mr: 2 }}
        >
          {id ? 'Atualizar Empresa' : 'Adicionar Empresa'}
        </Button>
        <Button
          type="button"
          variant="outlined"
          color="secondary"
          sx={{ mt: 2 }}
          onClick={onCancelEdit}
        >
          Cancelar
        </Button>
      </FormCard>
    </Container>
  );
}

export default EmpresaForm;
